import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/init-firebase';

/**
 * Shared hook that fetches all CRM-enabled users from Firestore.
 * Returns a normalized array, a uid->user Map, and a name-lookup helper.
 */
export function useCrmUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'users'), where('CRMUser', '==', true));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          name: data.Name || data.name || '',
          email: data.Email || data.email || '',
          crmRole: data.CRMRole || '',
          branch: data.Branch || '',
          isDTC: data.isDTCLoanOfficer === true,
        };
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(list);
    } catch (err) {
      console.error('[useCrmUsers] load error:', err);
      setError(err.message || 'Failed to load CRM users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const userMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.uid, u));
    return m;
  }, [users]);

  const getUserName = useCallback(
    (uid) => userMap.get(uid)?.name || uid,
    [userMap]
  );

  return { users, loading, error, reload: load, userMap, getUserName };
}
